<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'yieldManagement' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container">
    <EditYieldManagementDunningForm
      v-if="settingsDto"
      :settings-dto="settingsDto"
      @save="handleSave"
    />
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import {
  useUpdateYieldManagementSettings,
  useYieldManagementSettings,
} from '@/queries/use-yield-management-settings.ts';
import EditYieldManagementDunningForm from '@/views/yield-management/yield-management-settings/EditYieldManagementDunningForm.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { computed, watchEffect } from 'vue';
import { useUiStore } from '@/stores/ui.store.ts';
import { useI18n } from 'vue-i18n';
import type { UpdateYieldManagementSettingsDto } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';

const { t } = useI18n();
const uiStore = useUiStore();

watchEffect(() => {
  uiStore.setHeaderTitle(t('dunning'));
});

const { data: settings } = useYieldManagementSettings();

const settingsDto = computed<UpdateYieldManagementSettingsDto | undefined>(() =>
  settings.value
    ? {
        defaultPaymentTermDays: settings.value.defaultPaymentTermDays,
        defaultPaymentTermDayType: settings.value.defaultPaymentTermDayType,
      }
    : undefined,
);

const { mutateAsync: updateSettings } = useUpdateYieldManagementSettings();

const handleSave = async (values: UpdateYieldManagementSettingsDto) => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallySaveChanges'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('saveChanges'),
    denyButtonText: t('cancel'),
  });
  if (!isConfirmed) {
    return;
  }
  await updateSettings(values);
};
</script>

<i18n lang="json">
{
  "en": {
    "dunning": "Dunning",
    "reallySaveChanges": "Do you really want to save? This changes the payment term of all customers without their own payment term.",
    "saveChanges": "Save changes"
  },
  "de": {
    "dunning": "Mahnwesen",
    "reallySaveChanges": "Möchtest du wirklich speichern? Dies ändert das Zahlungsziel aller Kunden ohne eigenes Zahlungsziel.",
    "saveChanges": "Änderungen speichern"
  },
  "es": {
    "dunning": "Gestión de cobros",
    "reallySaveChanges": "¿Seguro que quieres guardar? Cambiará el plazo de pago de todos los clientes sin plazo de pago propio.",
    "saveChanges": "Guardar cambios"
  }
}
</i18n>
